/**
 * Client-side copy of the minimal status interpretation logic needed by v2.
 *
 * IMPORTANT:
 * - The web Docker build context is `./web`, so we cannot import `../../api/*`.
 * - This file intentionally mirrors `api/status-logic.mjs` for TOG/airborne parity.
 */

function cleanText(v) {
  if (v == null) return "";
  return String(v).trim();
}

function flightRowsPhaseFragments(flights) {
  if (!Array.isArray(flights)) return "";
  const chunks = [];
  for (const f of flights) {
    if (!f || typeof f !== "object") continue;
    if (f.cancelled === true || f.canceled === true) chunks.push("cancelled");
    chunks.push(
      cleanText(f.status),
      cleanText(f.phase),
      cleanText(f.flight_status),
      cleanText(f.status_text),
    );
  }
  return chunks.filter(Boolean).join(" ").toLowerCase();
}

function phaseTextRootFromStatus(st) {
  return [cleanText(st?.status_text), cleanText(st?.phase), cleanText(st?.status), cleanText(st?.flight_status)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function phaseTextAllFromStatus(st) {
  const root = phaseTextRootFromStatus(st);
  const fr = flightRowsPhaseFragments(st?.flights);
  return `${root} ${fr}`.trim().toLowerCase();
}

function statusIndicatesCancelled(st, phaseTextAll) {
  if (phaseTextAll.includes("cancel") || phaseTextAll.includes("cancelled") || phaseTextAll.includes("canceled")) {
    return true;
  }
  if (Array.isArray(st?.flights)) {
    for (const f of st.flights) {
      if (f && (f.cancelled === true || f.canceled === true)) return true;
    }
  }
  return false;
}

export function deriveMovementAt({ status, aircraft } = {}) {
  const normalise = (v) => {
    if (!v) return null;
    const s = String(v).trim();
    return s || null;
  };

  const on = normalise(status?.on);
  const off = normalise(status?.off);
  const manual = normalise(aircraft?.manual_status_last_flight);
  const updatedAt = normalise(status?.updated_at);

  const now = Date.now();
  const onStamp = on ? Date.parse(on) : NaN;
  const offStamp = off ? Date.parse(off) : NaN;
  const manualStamp = manual ? Date.parse(manual) : NaN;
  const updatedStamp = updatedAt ? Date.parse(updatedAt) : NaN;

  const onEqualsOff = Number.isFinite(onStamp) && Number.isFinite(offStamp) && onStamp === offStamp;

  const statusText = String(status?.status || status?.phase || "").toLowerCase();

  const departedAfterThisLanding =
    Number.isFinite(onStamp) &&
    Number.isFinite(offStamp) &&
    !onEqualsOff &&
    offStamp > onStamp + 60 * 1000;

  if (departedAfterThisLanding && Array.isArray(status?.flights)) {
    const flights = status.flights;
    let latestAfterOffIso = null;
    let latestAfterOffStamp = NaN;
    for (const flight of flights) {
      if (!flight || typeof flight !== "object") continue;
      const flightOn = normalise(
        flight.on ||
          flight.actual_arrival ||
          flight.actual_on ||
          flight.actual_in ||
          flight.actual_arrival_time ||
          null
      );
      if (!flightOn) continue;
      const flightOnStamp = Date.parse(flightOn);
      if (!Number.isFinite(flightOnStamp) || flightOnStamp > now) continue;
      if (Number.isFinite(offStamp) && flightOnStamp >= offStamp - 60 * 1000) {
        if (!Number.isFinite(latestAfterOffStamp) || flightOnStamp > latestAfterOffStamp) {
          latestAfterOffStamp = flightOnStamp;
          latestAfterOffIso = flightOn;
        }
      }
    }
    if (latestAfterOffIso) {
      return latestAfterOffIso;
    }
  }

  if (Number.isFinite(onStamp) && onStamp <= now && !onEqualsOff && !departedAfterThisLanding) {
    return on;
  }
  if (Number.isFinite(offStamp) && offStamp <= now) {
    return off;
  }

  if ((statusText.includes("arrived") || statusText.includes("landed")) && status?.flights) {
    const flights = Array.isArray(status.flights) ? status.flights : [];
    let latestLanding = null;
    for (const flight of flights) {
      const flightOn = normalise(flight.on || flight.actual_arrival);
      if (flightOn) {
        const flightOnStamp = Date.parse(flightOn);
        if (Number.isFinite(flightOnStamp) && flightOnStamp <= now) {
          if (!latestLanding || flightOnStamp > Date.parse(latestLanding)) {
            latestLanding = flightOn;
          }
        }
      }
    }
    if (latestLanding) return latestLanding;
  }

  if (Number.isFinite(manualStamp)) {
    if (Number.isFinite(updatedStamp) && now - updatedStamp <= 48 * 60 * 60 * 1000) {
      return null;
    }
    return manual;
  }
  return null;
}

export function calculateGroundedDuration(movementAt, isAirborne, now = Date.now()) {
  if (isAirborne) return 0;
  if (!movementAt) return -1;
  const stamp = Date.parse(movementAt);
  if (!Number.isFinite(stamp)) return -1;
  if (stamp > now) return -1;
  return Math.max(0, Math.round((now - stamp) / 60000));
}

/** Stop calling a FlightAware sector airborne after this long with no landing (stuck cache / result unknown). */
export const STALE_AIRBORNE_MAX_MS = 36 * 60 * 60 * 1000;

function latestPastMs(values, now) {
  let best = null;
  for (const v of values) {
    if (v == null || v === "") continue;
    const ts = typeof v === "number" && Number.isFinite(v) ? v : Date.parse(String(v));
    if (!Number.isFinite(ts) || ts > now) continue;
    if (best == null || ts > best) best = ts;
  }
  return best;
}

function groundedMinutesFromStaleOnEqualsOff(aircraft, onMs, now) {
  const last = latestPastMs([aircraft?.last_flight_at, aircraft?.manual_status_last_flight, onMs], now);
  if (last == null) return -1;
  return Math.max(0, Math.round((now - last) / 60000));
}

export const FR24_NEWER_THAN_FA_MS = 5 * 60 * 1000;

export function fr24PreferredGroundedMinutes(aircraft, faMovementAtIso, isAirborne, now = Date.now()) {
  if (isAirborne) return null;
  const src = cleanText(aircraft?.last_location_source).toUpperCase();
  // "FR24" and "FR24 Last Flight" (set when FA history was the trigger for dual-check).
  if (!src.startsWith("FR24")) return null;
  const frTs = Date.parse(aircraft?.last_flight_at);
  if (!Number.isFinite(frTs) || frTs > now) return null;
  const faTs = faMovementAtIso ? Date.parse(String(faMovementAtIso)) : NaN;
  if (Number.isFinite(faTs) && frTs <= faTs + FR24_NEWER_THAN_FA_MS) return null;
  return Math.max(0, Math.round((now - frTs) / 60000));
}

export function getGroundedMinutesAndLocation(aircraft, statusEntry, now = Date.now()) {
  const toTimestamp = (value) => {
    if (value == null) return null;
    const stamp = Date.parse(String(value));
    return Number.isFinite(stamp) ? stamp : null;
  };

  let groundedMinutes = -1;
  let lastLocation = cleanText(aircraft?.last_known_location) || cleanText(aircraft?.base) || "—";
  let statusLabel = cleanText(aircraft?.manual_status_text) || cleanText(aircraft?.tam_status) || "—";
  const hasManual = !!(aircraft?.manual_status_text || aircraft?.manual_status_last_flight || aircraft?.manual_status_location);
  if (aircraft?.manual_status_location) lastLocation = cleanText(aircraft.manual_status_location);

  if (statusEntry && statusEntry.status) {
    const st = statusEntry.status;
    const phaseTextRoot = phaseTextRootFromStatus(st);
    const phaseTextAll = phaseTextAllFromStatus(st);
    const hasLand = Boolean(st.on || st.arrival);

    const onMs = toTimestamp(st.on);
    const offMs = toTimestamp(st.off);
    const onEqualsOff = onMs !== null && offMs !== null && onMs === offMs;

    const hasDepartureInPast = offMs !== null && offMs <= now;

    const isDiverted = phaseTextAll.includes("diverted");
    const isCancelled = statusIndicatesCancelled(st, phaseTextAll);
    const phaseSuggestsAirborne =
      !isDiverted &&
      !isCancelled &&
      (phaseTextRoot.includes("enroute") ||
        phaseTextRoot.includes("en route") ||
        phaseTextRoot.includes("airborne") ||
        phaseTextRoot.includes("depart") ||
        phaseTextRoot.includes("departed") ||
        (hasDepartureInPast && !hasLand));

    let lastLandingTs = onMs;
    let lastTakeoffTs = offMs;
    if ((lastLandingTs === null || lastTakeoffTs === null) && Array.isArray(st?.flights)) {
      for (const flight of st.flights) {
        if (!flight || typeof flight !== "object") continue;
        if (lastLandingTs === null) {
          const candidateLanding = toTimestamp(flight.on || flight.actual_arrival || flight.actual_on || flight.actual_in);
          if (candidateLanding !== null) lastLandingTs = candidateLanding;
        }
        if (lastTakeoffTs === null) {
          const candidateTakeoff = toTimestamp(
            flight.off || flight.actual_departure || flight.actual_off || flight.actual_out
          );
          if (candidateTakeoff !== null) lastTakeoffTs = candidateTakeoff;
        }
        if (lastLandingTs !== null && lastTakeoffTs !== null) break;
      }
    }

    const hasLandingAfterTakeoff =
      lastLandingTs !== null && lastTakeoffTs !== null && lastLandingTs >= lastTakeoffTs - 60 * 1000;

    let airborneMinutes = null;
    if (
      lastTakeoffTs !== null &&
      lastTakeoffTs <= now &&
      (lastLandingTs === null || !hasLandingAfterTakeoff)
    ) {
      airborneMinutes = Math.max(0, Math.round((now - lastTakeoffTs) / 60000));
    }

    let isAirborne = phaseSuggestsAirborne;
    if (!isAirborne && airborneMinutes !== null && airborneMinutes <= 36 * 60) isAirborne = true;
    if (isAirborne && airborneMinutes !== null && airborneMinutes > 36 * 60) isAirborne = false;
    if (isAirborne && hasLandingAfterTakeoff && lastLandingTs !== null) {
      if (now - lastLandingTs > 5 * 60 * 1000) {
        isAirborne = false;
        airborneMinutes = null;
      }
    }
    if (isCancelled) {
      isAirborne = false;
    }

    let movementAt = null;
    if (!onEqualsOff) {
      movementAt = deriveMovementAt({ status: st, aircraft });
      groundedMinutes = calculateGroundedDuration(movementAt, isAirborne, now);
      if (!isAirborne && aircraft?.manual_status_last_flight && !movementAt) {
        const manualTs = Date.parse(aircraft.manual_status_last_flight);
        if (Number.isFinite(manualTs)) groundedMinutes = Math.max(0, Math.round((now - manualTs) / 60000));
      }
    } else if (isCancelled) {
      movementAt = deriveMovementAt({ status: st, aircraft });
      groundedMinutes = calculateGroundedDuration(movementAt, isAirborne, now);
      if (!isAirborne && aircraft?.manual_status_last_flight && !movementAt) {
        const manualTs = Date.parse(aircraft.manual_status_last_flight);
        if (Number.isFinite(manualTs)) groundedMinutes = Math.max(0, Math.round((now - manualTs) / 60000));
      }
    } else {
      // on===off: FA "result unknown". Recent pair = still airborne (HZ-FAE). Stale pair = on the ground (XY-AML / MSN 1521).
      if (onMs !== null && now - onMs <= STALE_AIRBORNE_MAX_MS) {
        groundedMinutes = 0;
      } else {
        groundedMinutes = groundedMinutesFromStaleOnEqualsOff(aircraft, onMs, now);
      }
    }

    if (phaseTextRoot && !phaseTextRoot.includes("unknown")) {
      statusLabel = (st.status_text || st.phase || st.status || statusLabel) || "—";
    }
  } else if (hasManual && aircraft?.manual_status_last_flight) {
    const ts = Date.parse(aircraft.manual_status_last_flight);
    if (Number.isFinite(ts)) groundedMinutes = Math.max(0, Math.round((now - ts) / 60000));
  }

  const faMovementForFr24 =
    statusEntry?.status && typeof statusEntry.status === "object"
      ? deriveMovementAt({ status: statusEntry.status, aircraft })
      : null;
  const isAirborneNow = groundedMinutes === 0;
  const fr24Tog = fr24PreferredGroundedMinutes(aircraft, faMovementForFr24, isAirborneNow, now);
  if (fr24Tog != null) {
    groundedMinutes = fr24Tog;
    if (!hasManual && cleanText(aircraft?.last_known_location)) {
      lastLocation = cleanText(aircraft.last_known_location);
    }
  } else if (groundedMinutes < 0) {
    const frOnly = fr24PreferredGroundedMinutes(aircraft, null, false, now);
    if (frOnly != null) {
      groundedMinutes = frOnly;
      if (!hasManual && cleanText(aircraft?.last_known_location)) {
        lastLocation = cleanText(aircraft.last_known_location);
      }
    }
  }

  // Last recorded landing on the aircraft row — prevails when FA only has scheduled /
  // result-unknown (no on/off) or FR24 did not supply a stamp.
  if (groundedMinutes < 0) {
    const lastFlightTs = Date.parse(String(aircraft?.last_flight_at || ""));
    if (Number.isFinite(lastFlightTs) && lastFlightTs <= now) {
      groundedMinutes = Math.max(0, Math.round((now - lastFlightTs) / 60000));
    }
  }

  return { groundedMinutes, lastLocation, statusLabel };
}

/** FA interpretation for no-flights-5d (mirrors api/status-logic.mjs). */
export function buildNoFlightsFaInterp(aircraft, statusEntry, nowMs = Date.now()) {
  const statusObj = statusEntry?.status;
  if (!statusObj || typeof statusObj !== "object") {
    const frOnly = fr24PreferredGroundedMinutes(aircraft, null, false, nowMs);
    if (frOnly != null) return { airborne: false, timeOnGroundMinutes: frOnly };
    const lastFlightTs = Date.parse(String(aircraft?.last_flight_at || ""));
    if (Number.isFinite(lastFlightTs) && lastFlightTs <= nowMs) {
      return {
        airborne: false,
        timeOnGroundMinutes: Math.max(0, Math.round((nowMs - lastFlightTs) / 60000)),
      };
    }
    return { airborne: false, timeOnGroundMinutes: null };
  }
  const phase = cleanText(
    statusObj.phase || statusObj.status_text || statusObj.flight_status || statusObj.status || "",
  );
  const phaseTextAll = phaseTextAllFromStatus(statusObj);
  const looksCancelled = statusIndicatesCancelled(statusObj, phaseTextAll);
  const { groundedMinutes } = getGroundedMinutesAndLocation(aircraft, statusEntry, nowMs);

  if (groundedMinutes === 0 && looksCancelled) {
    return { airborne: false, timeOnGroundMinutes: null };
  }
  if (groundedMinutes === 0) {
    const onTs = Date.parse(String(statusObj.on || statusObj.actual_on || ""));
    const offTs = Date.parse(String(statusObj.off || statusObj.actual_off || ""));
    const pairEqual = Number.isFinite(onTs) && Number.isFinite(offTs) && onTs === offTs;
    if (pairEqual && nowMs - onTs > STALE_AIRBORNE_MAX_MS) {
      return { airborne: false, timeOnGroundMinutes: Math.max(0, Math.round((nowMs - onTs) / 60000)) };
    }
    return { airborne: true, timeOnGroundMinutes: null };
  }
  if (groundedMinutes > 0) {
    return { airborne: false, timeOnGroundMinutes: groundedMinutes };
  }

  const phaseLower = phase.toLowerCase();
  const airborneFallback =
    !looksCancelled &&
    (phaseLower.includes("enroute") ||
      phaseLower.includes("airborne") ||
      phaseLower.includes("depart") ||
      phaseLower.includes("takeoff"));

  let timeOnGroundMinutes = null;
  if (!airborneFallback) {
    const onIso = statusObj.on || statusObj.actual_on || null;
    const offIso = statusObj.off || statusObj.actual_off || null;
    const onTs = onIso ? Date.parse(onIso) : NaN;
    const offTs = offIso ? Date.parse(offIso) : NaN;
    if (Number.isFinite(onTs) && onTs <= nowMs && !(Number.isFinite(offTs) && offTs === onTs)) {
      timeOnGroundMinutes = Math.max(0, Math.round((nowMs - onTs) / 60000));
    }
    if (timeOnGroundMinutes == null) {
      const lastFlightTs = Date.parse(String(aircraft?.last_flight_at || ""));
      if (Number.isFinite(lastFlightTs) && lastFlightTs <= nowMs) {
        timeOnGroundMinutes = Math.max(0, Math.round((nowMs - lastFlightTs) / 60000));
      }
    }
  }
  return { airborne: airborneFallback, timeOnGroundMinutes };
}

/**
 * On-ground location from FA origin/destination.
 * Prefer destination for arrival-like phases and for FlightAware "result unknown"
 * / on===off sectors (completed legs where FA never published a real landing time) —
 * otherwise we wrongly show the takeoff airport (e.g. HRG instead of CAI on MSN 1171).
 */
export function groundedFaEndpointLabel(origin, dest, phaseBlobLower, status = null) {
  const clean = (v) => (v == null ? "" : String(v).trim());
  const o = clean(origin);
  const d = clean(dest);
  if (!o && !d) return "";
  const t = (phaseBlobLower || "").toLowerCase();
  const arrivedLike =
    t.includes("arriv") ||
    t.includes("landed") ||
    t.includes("land ") ||
    t.includes("on ground") ||
    t.includes("at gate") ||
    t.includes("gate arr") ||
    t.includes("taxi to gate") ||
    t.includes("diverted");
  if (arrivedLike) {
    if (d) return d;
    if (o) return o;
    return "";
  }

  const resultUnknown = t.includes("result unknown");
  const onIso = status?.on || status?.actual_on || null;
  const offIso = status?.off || status?.actual_off || null;
  const onMs = onIso ? Date.parse(String(onIso)) : NaN;
  const offMs = offIso ? Date.parse(String(offIso)) : NaN;
  const onEqualsOff = Number.isFinite(onMs) && Number.isFinite(offMs) && onMs === offMs;
  // Ambiguous completed sector: destination is where the aircraft was going / last landed.
  if ((resultUnknown || onEqualsOff) && d) return d;

  if (o) return o;
  if (d) return d;
  return "";
}

